import { cn } from '@eriko/ui/src/lib/utils';
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useOrgDetail } from '../hooks/useReview';
import { OrgSidebar } from './orgs/OrgSidebar';
import { BooksTab } from './orgs/tabs/BooksTab';
import { ConversationsTab } from './orgs/tabs/ConversationsTab';
import { DocumentsTab } from './orgs/tabs/DocumentsTab';
import { ReconciliationTab } from './orgs/tabs/ReconciliationTab';
import { ReportsTab } from './orgs/tabs/ReportsTab';
import { TaxTab } from './orgs/tabs/TaxTab';

type TabKey = 'books' | 'conversations' | 'documents' | 'reports' | 'reconciliation' | 'tax';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'books', label: 'Books' },
  { key: 'conversations', label: 'Conversations' },
  { key: 'documents', label: 'Documents' },
  { key: 'reports', label: 'Reports' },
  { key: 'reconciliation', label: 'Reconciliation' },
  { key: 'tax', label: 'Tax' },
];

const VALID_TABS: TabKey[] = ['books', 'conversations', 'documents', 'reports', 'reconciliation', 'tax'];

function parseTabParam(raw: string | null): TabKey {
  if (raw && (VALID_TABS as string[]).includes(raw)) return raw as TabKey;
  return 'books';
}

export function OrgDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [searchParams] = useSearchParams();
  const { data: org } = useOrgDetail(orgId!);
  const [tab, setTab] = useState<TabKey>(() => parseTabParam(searchParams.get('tab')));

  if (!orgId) return null;

  return (
    <div className="flex flex-1 min-h-0">
      <OrgSidebar
        orgId={orgId}
        orgName={org?.name ?? 'Organization'}
        slug={org?.slug ?? ''}
        createdAt={org?.createdAt ?? null}
        ownerEmail={org?.ownerEmail ?? null}
        connections={(org?.connections ?? []).map((c) => ({
          provider: c.provider,
          status: c.isActive ? 'CONNECTED' : 'DISCONNECTED',
        }))}
        trialEndsAt={org?.trialEndsAt ?? null}
        subscriptionStatus={org?.subscriptionStatus ?? null}
        trialExtendedAt={org?.trialExtendedAt ?? null}
        trialExtendedReason={org?.trialExtendedReason ?? null}
      />
      <main className="flex-1 flex flex-col min-w-0">
        <nav className="flex gap-0 border-b border-surface-200 bg-surface-0 px-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                'relative px-4 py-3 text-sm font-medium',
                tab === t.key ? 'text-surface-900' : 'text-surface-500 hover:text-surface-700',
              )}
            >
              {t.label}
              {tab === t.key && (
                <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-surface-900" />
              )}
            </button>
          ))}
        </nav>
        <div className="flex-1 overflow-auto p-6 flex flex-col min-h-0">
          {tab === 'books' && <BooksTab orgId={orgId} />}
          {tab === 'conversations' && <ConversationsTab orgId={orgId} />}
          {tab === 'documents' && <DocumentsTab orgId={orgId} />}
          {tab === 'reports' && <ReportsTab orgId={orgId} />}
          {tab === 'reconciliation' && <ReconciliationTab orgId={orgId} />}
          {tab === 'tax' && <TaxTab orgId={orgId} />}
        </div>
      </main>
    </div>
  );
}
